import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { OutfitCard } from "@/components/OutfitCard";
import { weatherIcon } from "@/components/WeatherWidget";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Sparkles, RefreshCw, CalendarRange, Check } from "lucide-react";
import { toast } from "sonner";

const OCCASIONS = ["any", "casual", "work", "date", "gym", "formal"];
const TIMES = ["morning", "afternoon", "evening"];

export default function Planner() {
  const navigate = useNavigate();
  const [occasion, setOccasion] = useState("any");
  const [time, setTime] = useState("morning");
  const [today, setToday] = useState(null);
  const [week, setWeek] = useState(null);
  const [loadingToday, setLoadingToday] = useState(false);
  const [loadingWeek, setLoadingWeek] = useState(false);

  const payload = () => ({ time_of_day: time, occasion: occasion === "any" ? null : occasion });

  const genToday = async () => {
    setLoadingToday(true);
    try { setToday(await api.outfitToday(payload())); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate"); }
    finally { setLoadingToday(false); }
  };
  const genWeek = async () => {
    setLoadingWeek(true);
    try { const r = await api.outfitWeek(payload()); setWeek(r.days); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not generate"); }
    finally { setLoadingWeek(false); }
  };

  const wear = async (outfit) => {
    try {
      await api.createWearLog({ date: outfit.date, item_ids: outfit.item_ids, occasion: outfit.occasion, outfit_plan_id: outfit.id });
      toast.success("Saved to history");
    } catch { toast.error("Could not save"); }
  };

  return (
    <div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.02em]">Outfit Planner</h1>
      <p className="text-muted-foreground mt-1">Weather-aware looks for today and the week ahead.</p>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div className="w-40">
          <Label className="text-xs text-muted-foreground">Occasion</Label>
          <Select value={occasion} onValueChange={setOccasion}>
            <SelectTrigger className="mt-1 capitalize" data-testid="planner-occasion-select"><SelectValue /></SelectTrigger>
            <SelectContent>{OCCASIONS.map((o) => <SelectItem key={o} value={o} className="capitalize">{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Label className="text-xs text-muted-foreground">Time of day</Label>
          <Select value={time} onValueChange={setTime}>
            <SelectTrigger className="mt-1 capitalize" data-testid="planner-time-select"><SelectValue /></SelectTrigger>
            <SelectContent>{TIMES.map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <Tabs defaultValue="today" className="mt-6" data-testid="planner-tabs">
        <TabsList>
          <TabsTrigger value="today">Today</TabsTrigger>
          <TabsTrigger value="week">Week Ahead</TabsTrigger>
        </TabsList>

        <TabsContent value="today" className="mt-5">
          <div className="rounded-[var(--radius)] border border-border bg-card shadow-soft p-5 md:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl">Today's look</h3>
              <Button onClick={genToday} disabled={loadingToday} className="rounded-full" data-testid="planner-generate-today-button">
                {loadingToday ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                <span className="ml-1.5">{today ? "Regenerate" : "Generate"}</span>
              </Button>
            </div>
            {loadingToday ? (
              <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)] gap-4"><Skeleton className="aspect-[3/4] rounded-xl"/><div className="grid grid-cols-2 gap-3">{[0,1,2,3].map((i)=><Skeleton key={i} className="aspect-[3/4] rounded-xl"/>)}</div></div>
            ) : today ? (
              <>
                <OutfitCard outfit={today} />
                <Button variant="secondary" className="mt-4 rounded-full" onClick={() => wear(today)} data-testid="planner-wear-today-button"><Check className="h-4 w-4 mr-1.5"/> I'll wear this</Button>
              </>
            ) : (
              <EmptyGen onAdd={() => navigate("/add")} />
            )}
          </div>
        </TabsContent>

        <TabsContent value="week" className="mt-5">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">7-day plan using the forecast.</p>
            <Button onClick={genWeek} disabled={loadingWeek} className="rounded-full" data-testid="planner-generate-week-button">
              {loadingWeek ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CalendarRange className="h-4 w-4" />}
              <span className="ml-1.5">{week ? "Regenerate week" : "Plan my week"}</span>
            </Button>
          </div>
          {loadingWeek ? (
            <div className="space-y-3">{[0,1,2].map((i)=><Skeleton key={i} className="h-40 rounded-[var(--radius)]"/>)}</div>
          ) : week ? (
            <div className="grid md:grid-cols-2 gap-4" data-testid="planner-week-grid">
              {week.map((d) => (
                <div key={d.date} className="rounded-[var(--radius)] border border-border bg-card shadow-soft p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="font-medium">{new Date(d.date).toLocaleDateString(undefined, { weekday: "long" })}</div>
                      <div className="text-xs text-muted-foreground">{new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                    </div>
                    {d.weather?.day && (
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        {weatherIcon(d.weather.day.code, "h-4 w-4")}
                        <span>{Math.round(d.weather.day.temp_max)}°/{Math.round(d.weather.day.temp_min)}°</span>
                      </div>
                    )}
                  </div>
                  <OutfitCard outfit={d} compact />
                  {d.reasoning && <p className="mt-3 text-xs text-muted-foreground">{d.reasoning}</p>}
                </div>
              ))}
            </div>
          ) : (
            <EmptyGen onAdd={() => navigate("/add")} week />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const EmptyGen = ({ onAdd, week }) => (
  <div className="rounded-xl border border-dashed border-border p-8 text-center">
    <Sparkles className="h-8 w-8 mx-auto text-action" />
    <p className="mt-3 text-sm text-muted-foreground">{week ? "Plan your week" : "Generate"} to see AI outfits. Make sure your closet has a few items first.</p>
    <Button variant="secondary" className="mt-4 rounded-full" onClick={onAdd}>Add clothing</Button>
  </div>
);
